export const ONBOARDING_LOGIN_ALERT_KEY = 'qc_onboarding_login_alert';

const ALERT_MAX_AGE_MS = 10 * 60 * 1000;

export interface OnboardingLoginAlertDetails {
  source: string;
  reason: string;
  at?: number;
}

export function isOnboardingPath(pathname?: string): boolean {
  const path = pathname ?? window.location.pathname;
  return path.startsWith('/onboarding');
}

export function buildOnboardingLoginAlertMessage(
  details: OnboardingLoginAlertDetails,
): string {
  return `Redirected to login. Source: ${details.source}. Reason: ${details.reason}`;
}

/**
 * Persists the redirect log (source + reason) before a full-page redirect to
 * login so the alert can be replayed on the login page. No-ops when the user
 * is not on an onboarding step.
 */
export function stashOnboardingLoginAlert(
  details: OnboardingLoginAlertDetails,
): void {
  if (!isOnboardingPath()) return;
  try {
    const raw = sessionStorage.getItem(ONBOARDING_LOGIN_ALERT_KEY);
    if (raw) {
      const existing: OnboardingLoginAlertDetails | null = JSON.parse(raw);
      // Keep the first (root cause) entry when several redirects chain.
      if (
        existing?.at &&
        Date.now() - existing.at < 5000 &&
        existing.source &&
        existing.reason
      ) {
        return;
      }
    }
    sessionStorage.setItem(
      ONBOARDING_LOGIN_ALERT_KEY,
      JSON.stringify({ ...details, at: Date.now() }),
    );
  } catch {
    /* ignore */
  }
}

/** Reads and clears the pending redirect log, if any (stale entries are dropped). */
export function consumeOnboardingLoginAlert(): OnboardingLoginAlertDetails | null {
  try {
    const raw = sessionStorage.getItem(ONBOARDING_LOGIN_ALERT_KEY);
    if (!raw) return null;
    sessionStorage.removeItem(ONBOARDING_LOGIN_ALERT_KEY);
    const parsed: OnboardingLoginAlertDetails | null = JSON.parse(raw);
    if (!parsed?.source || !parsed?.reason) return null;
    if (parsed.at && Date.now() - parsed.at > ALERT_MAX_AGE_MS) return null;
    return parsed;
  } catch {
    return null;
  }
}
